import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import useAdminAuth from '../hooks/useAdminAuth';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ConfirmModal from '../components/ConfirmModal';
import { Users, Mail, Phone, Building, Calendar, CheckCircle, XCircle, Shield, Filter, Eye, FileText, Truck as TruckIcon, X, Edit2, Trash2, Key, Ban, UserCheck, MapPin, Plus, DollarSign, Lock } from 'lucide-react';
import { formatDateAU, formatDateShortAU } from '../utils/dateFormat';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminUsers = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  useAdminAuth(); // Protect this route
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, shipper, carrier
  const [subscriptionFilter, setSubscriptionFilter] = useState('all'); // all, active, inactive
  const [docStatusFilter, setDocStatusFilter] = useState('all'); // all, complete, incomplete
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showInsuranceUploadModal, setShowInsuranceUploadModal] = useState(false);
  const [showFixRoleModal, setShowFixRoleModal] = useState(false);
  const [showApproveCarrierModal, setShowApproveCarrierModal] = useState(false);
  const [showDeleteUserModal, setShowDeleteUserModal] = useState(false);
  const [showUnsuspendModal, setShowUnsuspendModal] = useState(false);
  const [showDeleteDocModal, setShowDeleteDocModal] = useState(false);
  const [showEquipmentForm, setShowEquipmentForm] = useState(false);
  const [newEquipment, setNewEquipment] = useState({
    type: 'prime_mover', make: '', model: '', year: '', registration_number: '', state: 'NSW'
  });
  const [showDeleteEquipModal, setShowDeleteEquipModal] = useState(false);
  const [showEditEquipModal, setShowEditEquipModal] = useState(false);
  const [equipToEdit, setEquipToEdit] = useState(null);
  const [editEquipData, setEditEquipData] = useState({});
  const [carrierToApprove, setCarrierToApprove] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [userToUnsuspend, setUserToUnsuspend] = useState(null);
  const [docToDelete, setDocToDelete] = useState(null);
  const [equipToDelete, setEquipToDelete] = useState(null);
  const [fixRoleData, setFixRoleData] = useState({ user: null, recommendedRole: 'carrier' });
  const [editFormData, setEditFormData] = useState({});
  const [insuranceFormData, setInsuranceFormData] = useState({
    insurance_type: '',
    policy_number: '',
    insurer_name: '',
    expiry_date: '',
    document_file: null
  });
  const [userDocCounts, setUserDocCounts] = useState({}); // Track doc/equipment counts per user

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    filterUsers();
  }, [users, filter, subscriptionFilter, docStatusFilter, searchTerm, userDocCounts]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      const usersData = response.data;
      setUsers(usersData);
      
      // Backend now pre-joins insurance/equipment counts on every carrier user.
      // Fall back to a per-user fetch only if the backend didn't populate them (legacy safety).
      const docCounts = {};
      const missing = [];
      for (const user of usersData) {
        const isCarrier = (user.roles && user.roles.includes('carrier')) || user.role === 'carrier';
        if (!isCarrier) continue;
        if (user.insurance_count !== undefined || user.equipment_count !== undefined) {
          docCounts[user.email] = {
            insurance: user.insurance_count || 0,
            equipment: user.equipment_count || 0,
            uploadedTypes: user.uploaded_insurance_types || [],
          };
        } else {
          missing.push(user);
        }
      }

      if (missing.length > 0) {
        // Legacy fallback - fire ALL requests in parallel (was sequential)
        const results = await Promise.all(missing.map(async (user) => {
          try {
            const [ins, eq] = await Promise.all([
              axios.get(`${API}/insurance/carrier/${user.email}`).catch(() => ({ data: [] })),
              axios.get(`${API}/equipment/carrier/${user.email}`).catch(() => ({ data: [] })),
            ]);
            return [user.email, { insurance: ins.data.length, equipment: eq.data.length, uploadedTypes: ins.data.map(d => d.insurance_type) }];
          } catch (err) {
            return [user.email, { insurance: 0, equipment: 0, uploadedTypes: [] }];
          }
        }));
        for (const [email, counts] of results) {
          docCounts[email] = counts;
        }
      }
      setUserDocCounts(docCounts);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load users',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const filterUsers = () => {
    let filtered = users;

    // Filter by role (support both old 'role' field and new 'roles' array)
    if (filter !== 'all') {
      filtered = filtered.filter(user => {
        if (user.roles && Array.isArray(user.roles)) {
          return user.roles.includes(filter);
        }
        return user.role === filter;
      });
    }

    // Filter by subscription status (Active/Inactive carriers)
    if (subscriptionFilter !== 'all') {
      filtered = filtered.filter(user => {
        const isCarrier = (user.roles && user.roles.includes('carrier')) || user.role === 'carrier';
        // Subscription filter only applies to carriers; non-carriers pass through
        if (!isCarrier) return true;
        const isActive = user.subscription_status === 'active';
        return subscriptionFilter === 'active' ? isActive : !isActive;
      });
    }

    // Filter by document & equipment status (carriers only)
    if (docStatusFilter !== 'all') {
      const coreDocKeys = ['motor_fleet', 'public_liability', 'marine_carriers', 'workers_comp'];
      filtered = filtered.filter(user => {
        const isCarrier = (user.roles && user.roles.includes('carrier')) || user.role === 'carrier';
        // Doc-status filter only applies to carriers; non-carriers pass through
        if (!isCarrier) return true;
        const counts = userDocCounts[user.email];
        if (!counts) return docStatusFilter === 'incomplete'; // unknown -> treat as incomplete
        const uploaded = counts.uploadedTypes || [];
        const hasAllDocs = coreDocKeys.every(k => uploaded.includes(k));
        const hasEquipment = (counts.equipment || 0) > 0;
        const isComplete = hasAllDocs && hasEquipment;
        return docStatusFilter === 'complete' ? isComplete : !isComplete;
      });
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(user =>
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (user.company_name && user.company_name.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    setFilteredUsers(filtered);
  };

  const handleViewInsurance = async (email) => {
    try {
      const response = await axios.get(`${API}/insurance/carrier/${email}`);
      console.log('Insurance documents:', response.data);
      
      if (response.data.length === 0) {
        toast({
          title: 'No Insurance Documents',
          description: 'This carrier has not uploaded any insurance documents yet.'
        });
      } else {
        toast({
          title: 'Insurance Documents',
          description: `Found ${response.data.length} insurance document(s). Check console for details.`
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load insurance documents',
        variant: 'destructive'
      });
    }
  };

  const handleViewUserDetails = async (user) => {
    setSelectedUser(user);
    setShowDetailsModal(true);
    
    try {
      const [insuranceResponse, equipmentResponse, bankResponse, listingsResponse] = await Promise.all([
        axios.get(`${API}/insurance/carrier/${user.email}`).catch(() => ({ data: [] })),
        axios.get(`${API}/equipment/carrier/${user.email}`).catch(() => ({ data: [] })),
        axios.get(`${API}/users/${user.email}/bank-details`).catch(() => ({ data: {} })),
        axios.get(`${API}/admin/users/${encodeURIComponent(user.email)}/transporter-listings`).catch(() => ({ data: [] })),
      ]);
      
      // Merge bank details into selectedUser for display
      setSelectedUser(prev => ({ ...prev, ...bankResponse.data }));
      setUserDetails({
        insurance: insuranceResponse.data,
        equipment: equipmentResponse.data,
        transporterListings: listingsResponse.data,
      });
    } catch (error) {
      console.error('Error fetching user details:', error);
    }
  };

  const handleToggleListingHidden = async (listing, hidden, reason = '') => {
    try {
      await axios.patch(`${API}/admin/transporter-listings/${listing.id}/hide`, {
        hidden,
        reason,
        admin_email: getAdminEmail(),
      });
      // Refresh listings inside the modal
      const r = await axios.get(`${API}/admin/users/${encodeURIComponent(selectedUser.email)}/transporter-listings`);
      setUserDetails(prev => ({ ...prev, transporterListings: r.data }));
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to update listing visibility');
    }
  };

  const viewDocument = (documentId) => {
    // Extract the document ID from the stored:// URL if needed
    const docId = documentId.includes('stored://') ? documentId.split('/').pop() : documentId;
    window.open(`${API}/documents/view/${docId}`, '_blank');
  };


  const closeDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedUser(null);
    setUserDetails(null);
  };


  const handleEditUser = (user) => {
    setSelectedUser(user);
    setEditFormData({
      name: user.name,
      phone: user.phone,
      company_name: user.company_name || '',
      is_active: user.is_active
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    try {
      await axios.put(`${API}/users/${selectedUser.email}`, editFormData);
      
      toast({
        title: 'Success',
        description: 'User updated successfully'
      });
      
      setShowEditModal(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update user',
        variant: 'destructive'
      });
    }
  };

  const handleDeleteUser = async (user) => {
    setUserToDelete(user);
    setShowDeleteUserModal(true);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;
    
    setLoading(true);
    try {
      await axios.delete(`${API}/users/${userToDelete.email}`);
      
      toast({
        title: 'Success',
        description: 'User deleted successfully'
      });
      
      setShowDeleteUserModal(false);
      setUserToDelete(null);
      fetchUsers();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete user',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleApproveCarrier = async (user) => {
    // Show modal instead of browser confirm
    setCarrierToApprove(user);
    setShowApproveCarrierModal(true);
  };

  const handleActivateSubscription = async (user) => {
    if (!window.confirm(`Manually activate subscription for ${user.name || user.email}? This will set their status to active and allow them to quote.`)) return;
    try {
      await axios.put(`${API}/admin/users/${user.email}/activate-subscription`, { admin_email: getAdminEmail() });
      toast({ title: 'Success', description: `Subscription activated for ${user.email}` });
      fetchUsers();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to activate', variant: 'destructive' });
    }
  };

  const handleSetAdhoc = async (user) => {
    if (!window.confirm(`Set ${user.name || user.email} as an Adhoc Member? They will not need a subscription and will pay $49 per accepted job instead.`)) return;
    try {
      await axios.post(`${API}/users/${user.email}/set-adhoc-status`);
      toast({ title: 'Success', description: `${user.email} set as Adhoc Member (pay per job)` });
      fetchUsers();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to set adhoc', variant: 'destructive' });
    }
  };


  const handleToggleDocumentExemption = async (user) => {
    const isCurrentlyExempt = user.documents_exempt;
    const action = isCurrentlyExempt ? 'revoke' : 'grant';
    
    if (!window.confirm(`Are you sure you want to ${action} document exemption for ${user.name}?${!isCurrentlyExempt ? '\n\nThis will allow them to quote without all mandatory insurance documents.' : ''}`)) {
      return;
    }
    
    try {
      const adminEmail = localStorage.getItem('admin_email') || localStorage.getItem('adminEmail');
      
      const response = await axios.put(`${API}/admin/carriers/${user.email}/document-exemption`, null, {
        params: {
          admin_email: adminEmail,
          exempt: !isCurrentlyExempt,
          reason: !isCurrentlyExempt ? 'Admin exemption granted' : null
        }
      });
      
      toast({
        title: 'Success',
        description: response.data.message
      });
      
      fetchUsers();
    } catch (error) {
      console.error('Error toggling document exemption:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to update document exemption',
        variant: 'destructive'
      });
    }
  };

  const confirmApproveCarrier = async () => {
    if (!carrierToApprove) return;
    
    setLoading(true);
    try {
      const adminEmail = localStorage.getItem('admin_email') || localStorage.getItem('adminEmail');
      
      console.log('Approving carrier:', carrierToApprove.email);
      console.log('Admin email:', adminEmail);
      
      if (!adminEmail) {
        toast({
          title: 'Error',
          description: 'Admin email not found. Please log in again.',
          variant: 'destructive'
        });
        setLoading(false);
        return;
      }
      
      console.log('Making API call to approve carrier...');
      const response = await axios.put(`${API}/admin/carriers/${carrierToApprove.email}/approve`, null, {
        params: {
          admin_email: adminEmail
        }
      });
      
      console.log('Approval response:', response.data);
      
      toast({
        title: 'Success',
        description: `${carrierToApprove.name} has been approved as a carrier`
      });
      
      setShowApproveCarrierModal(false);
      setCarrierToApprove(null);
      fetchUsers();
    } catch (error) {
      console.error('Error approving carrier:', error);
      console.error('Error response:', error.response?.data);
      
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to approve carrier',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };


  const handleResetPassword = async (user) => {
    const newPassword = prompt(`Enter new password for ${user.name} (${user.email}):`);
    
    if (!newPassword) {
      return;
    }
    
    if (newPassword.length < 6) {
      toast({
        title: 'Error',
        description: 'Password must be at least 6 characters',
        variant: 'destructive'
      });
      return;
    }

    try {
      const adminEmail = localStorage.getItem('admin_email') || localStorage.getItem('adminEmail');
      const response = await axios.post(`${API}/admin/reset-user-password`, null, {
        params: {
          user_email: user.email,
          new_password: newPassword,
          admin_email: adminEmail
        }
      });
      
      toast({
        title: 'Success',
        description: `Password reset for ${user.name}. New password: ${newPassword}`
      });
      
      // Show an alert with the password so admin can copy it
      alert(`Password reset successfully!\n\nEmail: ${user.email}\nNew Password: ${newPassword}\n\nPlease provide this password to the user.`);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to reset password',
        variant: 'destructive'
      });
    }
  };


  const handleSuspendUser = async (user) => {
    const isSuspended = user.is_suspended;
    
    if (isSuspended) {
      // Unsuspend
      setUserToUnsuspend(user);
      setShowUnsuspendModal(true);
    } else {
      // Suspend
      const reason = prompt(`Enter reason for suspending ${user.name}'s account:\n\n(Examples: Expired insurance, Complaint investigation, Payment issue)`);
      
      if (!reason) {
        return;
      }

      try {
        const adminEmail = localStorage.getItem('admin_email') || localStorage.getItem('adminEmail');
        await axios.post(`${API}/admin/users/${user.email}/suspend`, null, {
          params: {
            admin_email: adminEmail,
            reason: reason,
            suspend: true
          }
        });
        
        toast({
          title: 'Success',
          description: `${user.name}'s account has been suspended`
        });
        
        fetchUsers();
      } catch (error) {
        toast({
          title: 'Error',
          description: 'Failed to suspend account',
          variant: 'destructive'
          });
      }
    }
  };

  const confirmUnsuspendUser = async () => {
    if (!userToUnsuspend) return;
    
    setLoading(true);
    try {
      const adminEmail = localStorage.getItem('admin_email') || localStorage.getItem('adminEmail');
      await axios.post(`${API}/admin/users/${userToUnsuspend.email}/suspend`, null, {
        params: {
          admin_email: adminEmail,
          reason: '',
          suspend: false
        }
      });
      
      toast({
        title: 'Success',
        description: `${userToUnsuspend.name}'s account has been unsuspended`
      });
      
      setShowUnsuspendModal(false);
      setUserToUnsuspend(null);
      fetchUsers();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to unsuspend account',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const confirmDeleteDoc = async () => {
    if (!docToDelete) return;
    
    setLoading(true);
    try {
      const adminEmail = localStorage.getItem('admin_email') || localStorage.getItem('adminEmail');
      await axios.delete(`${API}/insurance/${docToDelete.doc.id}`, {
        params: { user_email: adminEmail }
      });
      
      toast({
        title: 'Success',
        description: 'Document deleted successfully'
      });
      
      setShowDeleteDocModal(false);
      setDocToDelete(null);
      
      // Refresh user details if in modal
      if (selectedUser) {
        handleViewUserDetails(selectedUser);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete document',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const confirmDeleteEquip = async () => {
    if (!equipToDelete) return;
    
    setLoading(true);
    try {
      const adminEmail = localStorage.getItem('admin_email') || localStorage.getItem('adminEmail');
      await axios.delete(`${API}/equipment/${equipToDelete.equip.id}`, {
        params: { user_email: adminEmail }
      });
      
      toast({
        title: 'Success',
        description: 'Equipment deleted successfully'
      });
      
      setShowDeleteEquipModal(false);
      setEquipToDelete(null);
      
      if (selectedUser) {
        handleViewUserDetails(selectedUser);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete equipment',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditEquip = (equip) => {
    setEquipToEdit(equip);
    setEditEquipData({
      equipment_type: equip.equipment_type || 'prime_mover',
      description: equip.description || '',
      registration_number: equip.registration_number || '',
      registration_expiry: equip.registration_expiry || '',
      tare_mass: equip.tare_mass || '',
      payload_rating: equip.payload_rating || '',
      number_of_axles: equip.number_of_axles || '',
      number_of_tyres: equip.number_of_tyres || ''
    });
    setShowEditEquipModal(true);
  };

  const confirmEditEquip = async () => {
    if (!equipToEdit) return;
    if (!editEquipData.description || !editEquipData.registration_number) {
      toast({ title: 'Error', description: 'Description and registration number are required', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      const payload = { ...editEquipData };
      if (payload.tare_mass) payload.tare_mass = parseInt(payload.tare_mass);
      else delete payload.tare_mass;
      if (payload.payload_rating) payload.payload_rating = parseInt(payload.payload_rating);
      else delete payload.payload_rating;
      if (payload.number_of_axles) payload.number_of_axles = parseInt(payload.number_of_axles);
      else delete payload.number_of_axles;
      if (payload.number_of_tyres) payload.number_of_tyres = parseInt(payload.number_of_tyres);
      else delete payload.number_of_tyres;

      await axios.put(`${API}/equipment/${equipToEdit.id}`, payload);
      toast({ title: 'Success', description: 'Equipment updated successfully' });
      setShowEditEquipModal(false);
      setEquipToEdit(null);
      if (selectedUser) handleViewUserDetails(selectedUser);
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to update equipment', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };



  const handleOpenInsuranceUpload = (user) => {
    setSelectedUser(user);
    setInsuranceFormData({
      insurance_type: '',
      policy_number: '',
      insurer_name: '',
      expiry_date: '',
      document_file: null
    });
    setShowInsuranceUploadModal(true);
  };

  const handleAddEquipment = async () => {
    if (!newEquipment.make || !newEquipment.registration_number) {
      toast({ title: 'Error', description: 'Make and registration number are required', variant: 'destructive' });
      return;
    }
    if (!newEquipment.registration_expiry) {
      toast({ title: 'Error', description: 'Registration expiry date is required', variant: 'destructive' });
      return;
    }
    try {
      const description = [newEquipment.make, newEquipment.model, newEquipment.year].filter(Boolean).join(' ');
      await axios.post(`${API}/equipment`, {
        carrier_email: selectedUser.email,
        equipment_type: newEquipment.type,
        description: description || newEquipment.make,
        registration_number: newEquipment.registration_number,
        registration_expiry: newEquipment.registration_expiry
      });
      toast({ title: 'Success', description: 'Equipment added' });
      setShowEquipmentForm(false);
      setNewEquipment({ type: 'prime_mover', make: '', model: '', year: '', registration_number: '', registration_expiry: '', state: 'NSW' });
      handleViewUserDetails(selectedUser);
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to add equipment', variant: 'destructive' });
    }
  };


  const handleInsuranceFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: 'File Too Large',
          description: 'Document size must be less than 5MB',
          variant: 'destructive'
        });
        return;
      }
      
      // Validate file type
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
      if (!allowedTypes.includes(file.type)) {
        toast({
          title: 'Invalid File Type',
          description: 'Only PDF, JPG, and PNG files are allowed',
          variant: 'destructive'
        });
        return;
      }

      setInsuranceFormData(prev => ({ ...prev, document_file: file }));
    }
  };

  const handleInsuranceFormChange = (e) => {
    const { name, value } = e.target;
    setInsuranceFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmitInsurance = async (e) => {
    e.preventDefault();
    
    if (!insuranceFormData.document_file) {
      toast({
        title: 'Error',
        description: 'Please select a document file',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    try {
      // Convert file to base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Data = reader.result.split(',')[1];
        
        const uploadData = {
          carrier_email: selectedUser.email,
          insurance_type: insuranceFormData.insurance_type,
          policy_number: insuranceFormData.policy_number,
          insurer_name: insuranceFormData.insurer_name,
          expiry_date: insuranceFormData.expiry_date,
          document_base64: base64Data,
          file_name: insuranceFormData.document_file.name
        };

        await axios.post(`${API}/insurance/upload`, uploadData);
        
        toast({
          title: 'Success',
          description: 'Insurance document uploaded successfully'
        });
        
        setShowInsuranceUploadModal(false);
        if (showDetailsModal && selectedUser) {
          handleViewUserDetails(selectedUser); // Refresh details
        }
      };
      
      reader.readAsDataURL(insuranceFormData.document_file);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to upload insurance document',
        variant: 'destructive'
      });
      console.error('Insurance upload error:', error);
    } finally {
      setLoading(false);
    }
  };


  const handleFixUserRole = async (user) => {
    console.log('Fix Role button clicked for user:', user);
    
    // Check if user has missing/incomplete role
    const hasRoles = user.roles && user.roles.length > 0;
    const hasOldRole = user.role && !hasRoles;
    
    console.log('User role analysis:', { 
      hasRoles, 
      hasOldRole, 
      roles: user.roles, 
      role: user.role,
      subscription_status: user.subscription_status,
      payment_completed: user.payment_completed
    });
    
    if (hasRoles && !hasOldRole) {
      toast({
        title: 'No Fix Needed',
        description: 'This user already has proper role data'
      });
      return;
    }
    
    // Determine what role to assign
    let roleToAssign = 'carrier'; // Default assumption
    
    // Try to infer from subscription/payment data
    if (user.subscription_status || user.payment_completed || user.stripe_subscription_id) {
      roleToAssign = 'carrier';
    } else if (hasOldRole) {
      roleToAssign = user.role;
    }
    
    console.log('Recommended role:', roleToAssign);
    
    // Show modal instead of browser confirm
    setFixRoleData({ user, recommendedRole: roleToAssign });
    setShowFixRoleModal(true);
  };

  const confirmFixRole = async (role) => {
    const user = fixRoleData.user;
    console.log('Confirming role fix:', role, 'for user:', user.email);
    
    // Add loading state
    setLoading(true);
    
    try {
      // Try both variations of admin email storage key
      let adminEmail = localStorage.getItem('admin_email') || localStorage.getItem('adminEmail') || localStorage.getItem('adminEmail');
      console.log('Admin email from storage:', adminEmail);
      console.log('Checked keys: admin_email and adminEmail');
      
      if (!adminEmail) {
        toast({
          title: 'Error',
          description: 'Admin email not found. Please log out and log back in.',
          variant: 'destructive'
        });
        setLoading(false);
        return;
      }
      
      console.log('Making API call to fix role...');
      console.log('API endpoint:', `${API}/admin/users/${user.email}/fix-role`);
      console.log('Request params:', { admin_email: adminEmail, role: role });
      
      const response = await axios.post(`${API}/admin/users/${user.email}/fix-role`, null, {
        params: {
          admin_email: adminEmail,
          role: role
        }
      });
      
      console.log('API response:', response.data);
      
      toast({
        title: 'Success! ✓',
        description: `${user.name}'s role has been fixed as "${role}"`
      });
      
      // Close modal and refresh
      setShowFixRoleModal(false);
      setFixRoleData({ user: null, recommendedRole: 'carrier' });
      fetchUsers();
    } catch (error) {
      console.error('Error fixing user role:', error);
      console.error('Error details:', error.response?.data);
      
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to fix user role',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };



  const isCarrier = (u) => (u.roles && (u.roles.includes('carrier') || u.roles.includes('crane_operator'))) || u.role === 'carrier' || u.role === 'crane_operator';
  const isCraneOperator = (u) => (u.roles && u.roles.includes('crane_operator')) || u.role === 'crane_operator';
  const isShipper = (u) => (u.roles && u.roles.includes('shipper')) || u.role === 'shipper';

  const stats = {
    total: users.length,
    shippers: users.filter(u => isShipper(u)).length,
    carriers: users.filter(u => isCarrier(u)).length,
    activeSubscriptions: users.filter(u => u.subscription_status === 'active').length
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-12 text-center">
          <p>Loading users...</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="container mx-auto px-4 sm:px-6 py-12">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-3xl font-bold text-[#1e3a5f]">User Management</h1>
              <p className="text-gray-600 mt-2">Manage shippers and carriers</p>
            </div>
            <Button
              onClick={() => navigate('/admin')}
              variant="outline"
              className="border-gray-300"
            >
              Back to Dashboard
            </Button>
          </div>

          {/* Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Users</p>
                  <p className="text-3xl font-bold text-[#1e3a5f]">{stats.total}</p>
                </div>
                <Users className="w-10 h-10 text-blue-600" />
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Shippers</p>
                  <p className="text-3xl font-bold text-green-600">{stats.shippers}</p>
                </div>
                <Building className="w-10 h-10 text-green-600" />
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Carriers</p>
                  <p className="text-3xl font-bold text-orange-600">{stats.carriers}</p>
                </div>
                <Shield className="w-10 h-10 text-orange-600" />
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Active Subscriptions</p>
                  <p className="text-3xl font-bold text-purple-600">{stats.activeSubscriptions}</p>
                </div>
                <CheckCircle className="w-10 h-10 text-purple-600" />
              </div>
            </Card>
          </div>

          {/* Filters and Search */}
          <Card className="p-6 mb-6">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Filter by Role</Label>
                <div className="flex gap-2 mt-2 flex-wrap">
                  <Button
                    variant={filter === 'all' ? 'default' : 'outline'}
                    onClick={() => setFilter('all')}
                    size="sm"
                    data-testid="filter-role-all"
                  >
                    All Users
                  </Button>
                  <Button
                    variant={filter === 'shipper' ? 'default' : 'outline'}
                    onClick={() => setFilter('shipper')}
                    size="sm"
                    data-testid="filter-role-shipper"
                  >
                    Shippers
                  </Button>
                  <Button
                    variant={filter === 'carrier' ? 'default' : 'outline'}
                    onClick={() => setFilter('carrier')}
                    size="sm"
                    data-testid="filter-role-carrier"
                  >
                    Carriers
                  </Button>
                  <Button
                    variant={filter === 'crane_operator' ? 'default' : 'outline'}
                    onClick={() => setFilter('crane_operator')}
                    size="sm"
                    data-testid="filter-role-crane_operator"
                  >
                    Crane Operators
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => navigate('/admin/pilot-registrations')}
                    size="sm"
                    data-testid="filter-role-pilot"
                    className="border-dashed"
                    title="Pilots are managed on a dedicated page"
                  >
                    Pilots →
                  </Button>
                </div>
              </div>

              <div>
                <Label htmlFor="search">Search Users</Label>
                <Input
                  id="search"
                  placeholder="Search by name, email, or company..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="mt-2"
                  data-testid="users-search-input"
                />
              </div>
            </div>

            {/* Carrier-specific filters */}
            <div className="grid md:grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-200">
              <div>
                <Label className="flex items-center gap-1">
                  <Filter className="w-3 h-3" /> Carrier Subscription
                </Label>
                <div className="flex gap-2 mt-2 flex-wrap">
                  <Button
                    variant={subscriptionFilter === 'all' ? 'default' : 'outline'}
                    onClick={() => setSubscriptionFilter('all')}
                    size="sm"
                    data-testid="filter-sub-all"
                  >
                    All
                  </Button>
                  <Button
                    variant={subscriptionFilter === 'active' ? 'default' : 'outline'}
                    onClick={() => setSubscriptionFilter('active')}
                    size="sm"
                    className={subscriptionFilter === 'active' ? 'bg-green-600 hover:bg-green-700' : ''}
                    data-testid="filter-sub-active"
                  >
                    <CheckCircle className="w-3 h-3 mr-1" /> Active
                  </Button>
                  <Button
                    variant={subscriptionFilter === 'inactive' ? 'default' : 'outline'}
                    onClick={() => setSubscriptionFilter('inactive')}
                    size="sm"
                    className={subscriptionFilter === 'inactive' ? 'bg-orange-600 hover:bg-orange-700' : ''}
                    data-testid="filter-sub-inactive"
                  >
                    <XCircle className="w-3 h-3 mr-1" /> Inactive
                  </Button>
                </div>
              </div>

              <div>
                <Label className="flex items-center gap-1">
                  <FileText className="w-3 h-3" /> Documents & Equipment
                </Label>
                <div className="flex gap-2 mt-2 flex-wrap">
                  <Button
                    variant={docStatusFilter === 'all' ? 'default' : 'outline'}
                    onClick={() => setDocStatusFilter('all')}
                    size="sm"
                    data-testid="filter-docs-all"
                  >
                    All
                  </Button>
                  <Button
                    variant={docStatusFilter === 'complete' ? 'default' : 'outline'}
                    onClick={() => setDocStatusFilter('complete')}
                    size="sm"
                    className={docStatusFilter === 'complete' ? 'bg-green-600 hover:bg-green-700' : ''}
                    data-testid="filter-docs-complete"
                  >
                    <CheckCircle className="w-3 h-3 mr-1" /> Complete
                  </Button>
                  <Button
                    variant={docStatusFilter === 'incomplete' ? 'default' : 'outline'}
                    onClick={() => setDocStatusFilter('incomplete')}
                    size="sm"
                    className={docStatusFilter === 'incomplete' ? 'bg-red-600 hover:bg-red-700' : ''}
                    data-testid="filter-docs-incomplete"
                  >
                    <XCircle className="w-3 h-3 mr-1" /> Incomplete
                  </Button>
                </div>
              </div>
            </div>

            {/* Active filter chips / reset */}
            {(subscriptionFilter !== 'all' || docStatusFilter !== 'all' || filter !== 'all' || searchTerm) && (
              <div className="mt-4 pt-4 border-t border-gray-200 flex items-center gap-2 flex-wrap">
                <span className="text-xs text-gray-600">Active filters:</span>
                {filter !== 'all' && (
                  <span className="px-2 py-1 text-xs rounded bg-blue-50 text-blue-700 border border-blue-200 capitalize">
                    Role: {filter}
                  </span>
                )}
                {subscriptionFilter !== 'all' && (
                  <span className="px-2 py-1 text-xs rounded bg-blue-50 text-blue-700 border border-blue-200 capitalize">
                    Subscription: {subscriptionFilter}
                  </span>
                )}
                {docStatusFilter !== 'all' && (
                  <span className="px-2 py-1 text-xs rounded bg-blue-50 text-blue-700 border border-blue-200 capitalize">
                    Docs: {docStatusFilter}
                  </span>
                )}
                {searchTerm && (
                  <span className="px-2 py-1 text-xs rounded bg-blue-50 text-blue-700 border border-blue-200">
                    Search: "{searchTerm}"
                  </span>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setFilter('all');
                    setSubscriptionFilter('all');
                    setDocStatusFilter('all');
                    setSearchTerm('');
                  }}
                  className="text-xs h-7"
                  data-testid="filter-reset-btn"
                >
                  <X className="w-3 h-3 mr-1" /> Reset
                </Button>
              </div>
            )}
          </Card>

          {/* Users List */}
          <div className="space-y-4">
            <h2 className="text-xl font-bold text-[#1e3a5f] mb-4">
              {filter === 'all' ? 'All Users' : filter === 'shipper' ? 'Shippers' : filter === 'crane_operator' ? 'Crane Operators' : 'Carriers'} ({filteredUsers.length})
            </h2>

            {filteredUsers.length === 0 ? (
              <Card className="p-12 text-center">
                <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No users found matching your criteria</p>
              </Card>
            ) : (
              filteredUsers.map((user) => (
                <Card key={user.id} className="p-4 sm:p-6">
                  <div>
                    <div>
                      <div className="flex items-center gap-3 mb-3 flex-wrap">
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                          (user.roles && user.roles.includes('carrier')) || user.role === 'carrier' 
                            ? 'bg-orange-100 text-orange-800' 
                            : (user.roles && user.roles.includes('shipper')) || user.role === 'shipper'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {user.roles && user.roles.length > 0 
                            ? user.roles.join(', ') 
                            : user.role 
                            ? user.role 
                            : '⚠️ NO ROLE'}
                        </span>
                        
                        {((user.roles && user.roles.includes('carrier')) || user.role === 'carrier') && user.subscription_status && (
                          <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                            user.subscription_status === 'active' ? 'bg-purple-100 text-purple-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {user.subscription_status}
                          </span>
                        )}

                        {((user.roles && user.roles.includes('carrier')) || user.role === 'carrier') && user.subscription_plan && (
                          <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                            user.subscription_plan === 'adhoc' ? 'bg-orange-100 text-orange-800' : 'bg-blue-100 text-blue-800'
                          }`}>
                            {user.subscription_plan === 'monthly' ? 'Monthly' :
                             user.subscription_plan === 'quarterly' ? 'Quarterly' :
                             user.subscription_plan === 'yearly' ? 'Yearly' :
                             user.subscription_plan === 'adhoc' ? 'Ad Hoc' :
                             user.subscription_plan}
                          </span>
                        )}
                        {((user.roles && user.roles.includes('carrier')) || user.role === 'carrier') && !user.subscription_plan && (
                          <span className="px-3 py-1 bg-gray-100 text-gray-500 rounded-full text-sm font-semibold">
                            No Plan
                          </span>
                        )}
                        
                        {/* Carrier Approval Status Badge */}
                        {((user.roles && user.roles.includes('carrier')) || user.role === 'carrier') && (
                          <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                            user.approved_carrier 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {user.approved_carrier ? '✓ Approved' : '⏳ Pending Approval'}
                          </span>
                        )}
                        
                        {/* Document Upload Status Indicators */}
                        {((user.roles && user.roles.includes('carrier')) || user.role === 'carrier') && userDocCounts[user.email] && (
                          <>
                            <span className={`px-2 py-1 rounded text-xs font-medium flex items-center gap-1 ${
                              userDocCounts[user.email].insurance > 0 
                                ? 'bg-blue-50 text-blue-700 border border-blue-200' 
                                : 'bg-gray-50 text-gray-500 border border-gray-200'
                            }`}>
                              <FileText className="w-3 h-3" />
                              {userDocCounts[user.email].insurance} Docs
                            </span>
                            <span className={`px-2 py-1 rounded text-xs font-medium flex items-center gap-1 ${
                              userDocCounts[user.email].equipment > 0 
                                ? 'bg-blue-50 text-blue-700 border border-blue-200' 
                                : 'bg-gray-50 text-gray-500 border border-gray-200'
                            }`}>
                              <TruckIcon className="w-3 h-3" />
                              {userDocCounts[user.email].equipment} Equipment
                            </span>
                          </>
                        )}
                      </div>

                      {/* Missing Core Documents */}
                      {((user.roles && user.roles.includes('carrier')) || user.role === 'carrier') && userDocCounts[user.email] && (() => {
                        const coreDocs = [
                          { key: 'motor_fleet', label: 'Motor/Fleet' },
                          { key: 'public_liability', label: 'Public Liability' },
                          { key: 'marine_carriers', label: 'Marine/Carrier' },
                          { key: 'workers_comp', label: 'Workers Comp' }
                        ];
                        const uploaded = userDocCounts[user.email].uploadedTypes || [];
                        const missing = coreDocs.filter(d => !uploaded.includes(d.key));
                        if (missing.length === 0) return (
                          <div className="mb-2">
                            <span className="text-xs text-green-600 font-medium flex items-center gap-1">
                              <CheckCircle className="w-3 h-3" /> All core documents uploaded
                            </span>
                          </div>
                        );
                        return (
                          <div className="mb-2" data-testid={`missing-docs-${user.email}`}>
                            <p className="text-xs text-red-600 font-medium mb-1">Missing core documents:</p>
                            <div className="flex flex-wrap gap-1">
                              {missing.map(d => (
                                <span key={d.key} className="px-2 py-0.5 rounded text-xs bg-red-50 text-red-700 border border-red-200">
                                  {d.label}
                                </span>
                              ))}
                            </div>
                          </div>
                        );
                      })()}

                      <h3 className="text-lg font-bold text-gray-900 mb-2">{user.name}</h3>

                      <div className="grid md:grid-cols-3 gap-4 mb-3">
                        <div className="flex items-start gap-2">
                          <Mail className="w-4 h-4 text-gray-500 mt-1" />
                          <div>
                            <p className="text-xs text-gray-600">Email</p>
                            <p className="text-sm font-medium">{user.email}</p>
                          </div>
                        </div>

                        <div className="flex items-start gap-2">
                          <Phone className="w-4 h-4 text-gray-500 mt-1" />
                          <div>
                            <p className="text-xs text-gray-600">Phone</p>
                            <p className="text-sm font-medium">{user.phone}</p>
                          </div>
                        </div>

                        {user.company_name && (
                          <div className="flex items-start gap-2">
                            <Building className="w-4 h-4 text-gray-500 mt-1" />
                            <div>
                              <p className="text-xs text-gray-600">Company</p>
                              <p className="text-sm font-medium">{user.company_name}</p>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Password (Admin only) */}
                      <div className="mb-3 flex items-start gap-2">
                        <Lock className="w-4 h-4 text-gray-500 mt-1" />
                        <div>
                          <p className="text-xs text-gray-600">Password</p>
                          <p className="text-sm font-mono bg-gray-50 px-2 py-0.5 rounded" data-testid={`user-password-${user.email}`}>
                            {user.plain_password || '(not stored)'}
                          </p>
                        </div>
                      </div>

                      {/* Base Locations */}
                      {user.base_locations && user.base_locations.length > 0 && (
                        <div className="mt-2 mb-3">
                          <p className="text-xs text-gray-600 mb-1">Base Location(s):</p>
                          <div className="flex flex-wrap gap-1">
                            {user.base_locations.map((loc, idx) => (
                              <span 
                                key={idx} 
                                className={`px-2 py-0.5 rounded text-xs ${
                                  loc.is_primary 
                                    ? 'bg-green-100 text-green-800 font-semibold' 
                                    : 'bg-gray-100 text-gray-700'
                                }`}
                              >
                                {loc.suburb}, {loc.state}
                                {loc.is_primary && ' ★'}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          Joined: {formatDateShortAU(user.created_at)}
                        </div>
                        
                        {((user.roles && user.roles.includes('carrier')) || user.role === 'carrier') && user.subscription_expiry && (
                          <div className={`flex items-center gap-1 ${
                            new Date(user.subscription_expiry) < new Date() ? 'text-red-600 font-semibold' : ''
                          }`}>
                            <Calendar className="w-3 h-3" />
                            {new Date(user.subscription_expiry) < new Date() ? 'Expired: ' : 'Expires: '}
                            {formatDateShortAU(user.subscription_expiry)}
                          </div>
                        )}

                        {user.is_active ? (
                          <div className="flex items-center gap-1 text-green-600">
                            <CheckCircle className="w-3 h-3" />
                            Active
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 text-red-600">
                            <XCircle className="w-3 h-3" />
                            Inactive
                          </div>
                        )}
                        
                        {user.is_suspended && (
                          <div className="flex items-center gap-1 text-orange-600 font-semibold">
                            <Ban className="w-3 h-3" />
                            SUSPENDED
                          </div>
                        )}
                      </div>
                      
                      {user.is_suspended && user.suspension_reason && (
                        <div className="mt-2 text-xs text-orange-700 bg-orange-50 px-2 py-1 rounded">
                          Reason: {user.suspension_reason}
                        </div>
                      )}
                      
                      {/* Carrier Payment & Promo Code Info */}
                      {((user.roles && user.roles.includes('carrier')) || user.role === 'carrier') && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <div className="flex flex-wrap items-center gap-3 text-xs">
                            {/* Payment Status - Don't show for adhoc members */}
                            {user.subscription_status !== 'adhoc' && (
                              <div className={`flex items-center gap-1 px-2 py-1 rounded ${
                                user.payment_completed 
                                  ? 'bg-green-50 text-green-700 border border-green-200' 
                                  : !user.approved_carrier
                                  ? 'bg-gray-50 text-gray-600 border border-gray-200'
                                  : 'bg-red-50 text-red-700 border border-red-200'
                              }`}>
                                <span className="font-semibold">
                                  {user.payment_completed ? '✓ Payment Complete' : !user.approved_carrier ? '— Awaiting Approval' : '⚠ Payment Pending'}
                                </span>
                              </div>
                            )}
                            {user.subscription_status === 'adhoc' && (
                              <div className="flex items-center gap-1 px-2 py-1 rounded bg-blue-50 text-blue-700 border border-blue-200">
                                <span className="font-semibold">✓ Adhoc (Pay Per Job)</span>
                              </div>
                            )}
                            
                            {/* Promo Code Used */}
                            {user.promo_code_used && (
                              <div className="flex items-center gap-1 px-2 py-1 rounded bg-purple-50 text-purple-700 border border-purple-200">
                                <span className="font-semibold">🎟️ Promo:</span> {user.promo_code_used}
                              </div>
                            )}
                            
                            {/* Trial Info */}
                            {user.trial_end_date && (
                              <div className="flex items-center gap-1 px-2 py-1 rounded bg-blue-50 text-blue-700 border border-blue-200">
                                <span className="font-semibold">Trial Ends:</span> {formatDateShortAU(user.trial_end_date)}
                              </div>
                            )}
                            
                            {/* Additional Promo Codes Applied */}
                            {user.promo_codes_applied && user.promo_codes_applied.length > 0 && (
                              <div className="flex items-center gap-1 px-2 py-1 rounded bg-indigo-50 text-indigo-700 border border-indigo-200">
                                <span className="font-semibold">Additional Promos:</span> {user.promo_codes_applied.length}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {((user.roles && user.roles.includes('carrier')) || user.role === 'carrier') && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewUserDetails(user)}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                        {!user.approved_carrier && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleApproveCarrier(user)}
                            className="text-green-600 hover:text-green-700 border-green-300"
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Approve
                          </Button>
                        )}
                        {user.approved_carrier && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleToggleDocumentExemption(user)}
                            className={user.documents_exempt 
                              ? "text-purple-600 hover:text-purple-700 border-purple-300" 
                              : "text-gray-600 hover:text-gray-700 border-gray-300"}
                            title={user.documents_exempt ? "Revoke Document Exemption" : "Grant Document Exemption"}
                          >
                            <Shield className="w-4 h-4 mr-1" />
                            {user.documents_exempt ? 'Exempt' : 'Exemption'}
                          </Button>
                        )}
                        {user.subscription_status !== 'active' && user.subscription_status !== 'adhoc' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleActivateSubscription(user)}
                            className="text-green-600 hover:text-green-700 border-green-300 font-semibold"
                            title="Manually activate subscription"
                            data-testid={`activate-sub-${user.email}`}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Activate
                          </Button>
                        )}
                        {user.subscription_status !== 'adhoc' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleSetAdhoc(user)}
                            className="text-blue-600 hover:text-blue-700 border-blue-300 font-semibold"
                            title="Set as Adhoc Member (pay per job)"
                            data-testid={`set-adhoc-${user.email}`}
                          >
                            Set Adhoc
                          </Button>
                        )}
                      </div>
                    )}
                    
                    {/* Edit, Reset Password, Suspend, and Delete buttons for all users */}
                    <div className="flex flex-wrap gap-2 mt-2">
                      {/* Fix Role Button - Show for users with missing/incomplete roles */}
                      {(!user.roles || user.roles.length === 0) && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleFixUserRole(user)}
                          className="text-yellow-600 hover:text-yellow-700 border-yellow-300 font-semibold"
                          title="Fix Missing Role"
                        >
                          <Shield className="w-4 h-4 mr-1" />
                          Fix Role
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditUser(user)}
                        className="text-blue-600 hover:text-blue-700 border-blue-300"
                        title="Edit User"
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleResetPassword(user)}
                        className="text-orange-600 hover:text-orange-700 border-orange-300"
                        title="Reset Password"
                      >
                        <Key className="w-4 h-4" />
                      </Button>
                      {((user.roles && user.roles.includes('carrier')) || user.role === 'carrier') && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleSuspendUser(user)}
                          className={user.is_suspended 
                            ? "text-green-600 hover:text-green-700 border-green-300" 
                            : "text-orange-600 hover:text-orange-700 border-orange-300"
                          }
                          title={user.is_suspended ? "Unsuspend Account" : "Suspend Account"}
                        >
                          {user.is_suspended ? <UserCheck className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteUser(user)}
                        className="text-red-600 hover:text-red-700 border-red-300"
                        title="Delete User"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))
            )}
          </div>
        </div>
      </div>

      {/* User Details Modal */}
      {showDetailsModal && selectedUser && (
        <>
          <div 
            className="fixed inset-0 bg-black/50 z-50 backdrop-blur-sm"
            onClick={closeDetailsModal}
          />
          
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto">
            <div 
              className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto relative"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="sticky top-0 bg-white border-b p-6 flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold text-[#1e3a5f]">{selectedUser.name}</h2>
                  <p className="text-gray-600">{selectedUser.email}</p>
                </div>
                <button
                  onClick={closeDetailsModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-6 space-y-6">
                {/* User Basic Info */}
                <Card className="p-4 bg-blue-50">
                  <h3 className="font-semibold text-[#1e3a5f] mb-3">User Information</h3>
                  <div className="grid md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600">Phone</p>
                      <p className="font-medium">{selectedUser.phone}</p>
                    </div>
                    {selectedUser.company_name && (
                      <div>
                        <p className="text-gray-600">Company</p>
                        <p className="font-medium">{selectedUser.company_name}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-gray-600">Role</p>
                      <p className="font-medium capitalize">{selectedUser.role}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Status</p>
                      <p className="font-medium">{selectedUser.is_active ? 'Active' : 'Inactive'}</p>
                    </div>
                    {selectedUser.subscription_status && (
                      <>
                        <div>
                          <p className="text-gray-600">Subscription Status</p>
                          <p className="font-medium capitalize">{selectedUser.subscription_status}</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Subscription Plan</p>
                          <p className="font-medium capitalize">{selectedUser.subscription_plan || 'N/A'}</p>
                        </div>
                        {selectedUser.subscription_expiry && (
                          <div>
                            <p className="text-gray-600">Subscription Expires</p>
                            <p className="font-medium">{formatDateAU(selectedUser.subscription_expiry)}</p>
                          </div>
                        )}
                        {selectedUser.trial_end_date && (
                          <div>
                            <p className="text-gray-600">Trial End Date</p>
                            <p className="font-medium">{formatDateAU(selectedUser.trial_end_date)}</p>
                          </div>
                        )}
                        {selectedUser.promo_code_used && (
                          <div>
                            <p className="text-gray-600">Promo Code Used</p>
                            <p className="font-medium">{selectedUser.promo_code_used}</p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </Card>

                {/* Apply Promo Code Section - Only for Carriers */}
                {selectedUser && ((selectedUser.roles && selectedUser.roles.includes('carrier')) || selectedUser.role === 'carrier') && (
                  <Card className="p-4 bg-yellow-50 border-yellow-200">
                    <h3 className="font-semibold text-[#1e3a5f] mb-3">Apply Promo Code</h3>
                    <p className="text-sm text-gray-700 mb-3">
                      Apply a promotional code to extend this carrier's subscription or reactivate an expired account.
                    </p>
                    <div className="flex gap-2">
                      <Input
                        type="text"
                        placeholder="Enter promo code"
                        id="admin-promo-code-input"
                        className="flex-1"
                      />
                      <Button
                        size="sm"
                        onClick={async () => {
                          const promoInput = document.getElementById('admin-promo-code-input');
                          const promoCode = promoInput.value.trim();
                          
                          if (!promoCode) {
                            toast({
                              title: 'Error',
                              description: 'Please enter a promo code',
                              variant: 'destructive'
                            });
                            return;
                          }

                          try {
                            const adminEmail = localStorage.getItem('admin_email') || localStorage.getItem('adminEmail');
                            const response = await axios.post(`${API}/admin/apply-promo-to-user`, null, {
                              params: {
                                admin_email: adminEmail,
                                user_email: selectedUser.email,
                                promo_code: promoCode
                              }
                            });

                            toast({
                              title: 'Success',
                              description: response.data.message
                            });

                            promoInput.value = '';
                            fetchUsers(); // Refresh user list
                            handleViewUserDetails(selectedUser); // Refresh user details
                          } catch (error) {
                            const errorMsg = error.response?.data?.detail || 'Failed to apply promo code';
                            toast({
                              title: 'Error',
                              description: errorMsg,
                              variant: 'destructive'
                            });
                          }
                        }}
                        className="bg-red-600 hover:bg-red-700"
                      >
                        Apply Code
                      </Button>
                    </div>
                  </Card>
                )}

                {/* Bank Details (Admin Only) */}
                {selectedUser && ((selectedUser.roles && selectedUser.roles.includes('carrier')) || selectedUser.role === 'carrier') && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-[#1e3a5f] mb-3 flex items-center gap-2">
                      <DollarSign className="w-5 h-5" />
                      Bank Details
                    </h3>
                    <Card className="p-4 bg-gray-50">
                      {selectedUser.bank_bsb || selectedUser.bank_account_number ? (
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Account Name:</span>
                            <span className="font-medium">{selectedUser.bank_account_name || '—'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">BSB:</span>
                            <span className="font-medium">{selectedUser.bank_bsb || '—'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Account Number:</span>
                            <span className="font-medium">{selectedUser.bank_account_number || '—'}</span>
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm text-amber-700">Carrier has not provided bank details yet.</p>
                      )}
                    </Card>
                  </div>
                )}

                {/* Insurance Documents */}
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-lg font-semibold text-[#1e3a5f] flex items-center gap-2">
                      <FileText className="w-5 h-5" />
                      Insurance Documents ({userDetails?.insurance?.length || 0})
                    </h3>
                    {selectedUser && ((selectedUser.roles && selectedUser.roles.includes('carrier')) || selectedUser.role === 'carrier') && (
                      <Button
                        size="sm"
                        onClick={() => handleOpenInsuranceUpload(selectedUser)}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        Upload Document
                      </Button>
                    )}
                  </div>
                  
                  {!userDetails ? (
                    <p className="text-gray-500">Loading...</p>
                  ) : userDetails.insurance.length === 0 ? (
                    <Card className="p-6 bg-yellow-50 border-yellow-200">
                      <p className="text-gray-800 font-semibold mb-2">⚠️ No Documents Found</p>
                      <p className="text-sm text-gray-700">
                        This carrier has not uploaded any insurance documents yet.
                      </p>
                      <p className="text-sm text-gray-700 mt-2">
                        <strong>Action:</strong> You can upload insurance documents on their behalf using the button above, or ask them to upload through their dashboard.
                      </p>
                    </Card>
                  ) : (
                    <div className="space-y-3">
                      {userDetails.insurance.map((doc) => (
                        <Card key={doc.id} className="p-4">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <h4 className="font-semibold text-gray-900">
                                  {doc.insurance_type.replace(/_/g, ' ').toUpperCase()}
                                </h4>
                                {doc.verified ? (
                                  <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                                    Verified
                                  </span>
                                ) : (
                                  <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                                    Pending
                                  </span>
                                )}
                              </div>
                              <div className="grid grid-cols-2 gap-3 text-sm">
                                <div>
                                  <p className="text-gray-600">Policy Number</p>
                                  <p className="font-medium">{doc.policy_number}</p>
                                </div>
                                <div>
                                  <p className="text-gray-600">Insurer</p>
                                  <p className="font-medium">{doc.insurer_name}</p>
                                </div>
                                <div>
                                  <p className="text-gray-600">Expiry Date</p>
                                  <p className="font-medium">{doc.expiry_date}</p>
                                </div>
                                <div>
                                  <p className="text-gray-600">Uploaded</p>
                                  <p className="font-medium">{formatDateAU(doc.uploaded_at)}</p>
                                </div>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => viewDocument(doc.id)}
                              >
                                View Document
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setDocToDelete({ doc, userEmail: selectedUser.email });
                                  setShowDeleteDocModal(true);
                                }}
                                className="text-red-600 hover:text-red-700 border-red-300"
                              >
                                Delete
                              </Button>
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>

                {/* Transporter Listings */}
                {selectedUser && ((selectedUser.roles && selectedUser.roles.includes('carrier')) || selectedUser.role === 'carrier') && (
                  <div>
                    <h3 className="text-lg font-semibold text-[#1e3a5f] flex items-center gap-2 mb-3">
                      <TruckIcon className="w-5 h-5" />
                      Transporter Listings ({userDetails?.transporterListings?.length || 0})
                    </h3>
                    {!userDetails ? (
                      <p className="text-gray-500">Loading…</p>
                    ) : (userDetails.transporterListings || []).length === 0 ? (
                      <Card className="p-4 text-center">
                        <p className="text-gray-600 text-sm">No transporter listings.</p>
                      </Card>
                    ) : (
                      <div className="space-y-2">
                        {userDetails.transporterListings.map(l => (
                          <Card
                            key={l.id}
                            className={`p-3 border-l-4 ${l.hidden_by_admin ? 'border-l-red-500 bg-red-50/50' : (l.looks_like_name ? 'border-l-yellow-500 bg-yellow-50/40' : 'border-l-blue-500')}`}
                            data-testid={`admin-user-listing-${l.id}`}
                          >
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  {l.fleet_number && (
                                    <span
                                      className="text-[11px] font-bold px-2 py-0.5 rounded bg-[#1e3a5f] text-white font-mono"
                                      data-testid={`admin-user-listing-fleet-${l.id}`}
                                    >
                                      #{l.fleet_number}
                                    </span>
                                  )}
                                  {l.looks_like_name && !l.hidden_by_admin && (
                                    <span
                                      className="text-[10px] bg-yellow-400 text-yellow-900 px-2 py-0.5 rounded font-semibold uppercase"
                                      title="Fleet # matches a common first name"
                                      data-testid={`admin-user-name-flag-${l.id}`}
                                    >
                                      ⚠ Name?
                                    </span>
                                  )}
                                  <span className="font-semibold text-sm text-[#1e3a5f]">{l.location_suburb}, {l.location_state}</span>
                                  {l.hidden_by_admin && (
                                    <span className="text-[10px] bg-red-600 text-white px-2 py-0.5 rounded font-semibold uppercase">Hidden</span>
                                  )}
                                  {l.status === 'pending_payment' && (
                                    <span className="text-[10px] bg-amber-200 text-amber-900 px-2 py-0.5 rounded uppercase">Pending payment</span>
                                  )}
                                  {l.status === 'expired' && (
                                    <span className="text-[10px] bg-gray-200 text-gray-700 px-2 py-0.5 rounded uppercase">Expired</span>
                                  )}
                                </div>
                                <div className="text-xs text-gray-600 mt-1">
                                  {(l.trailer_types || []).join(', ')}
                                </div>
                                {l.hidden_by_admin && l.hidden_reason && (
                                  <p className="text-xs text-red-700 mt-1 italic">Reason: {l.hidden_reason}</p>
                                )}
                              </div>
                              <div className="flex-shrink-0">
                                {l.hidden_by_admin ? (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleToggleListingHidden(l, false)}
                                    className="text-blue-600 border-blue-500 hover:bg-blue-50 text-xs"
                                    data-testid={`admin-user-unhide-${l.id}`}
                                  >
                                    Un-hide
                                  </Button>
                                ) : (
                                  <Button
                                    size="sm"
                                    onClick={() => {
                                      const reason = window.prompt('Reason for hiding this listing (optional):', 'Fleet # contains a personal name');
                                      if (reason === null) return;
                                      handleToggleListingHidden(l, true, reason);
                                    }}
                                    className="bg-red-600 hover:bg-red-700 text-white text-xs"
                                    data-testid={`admin-user-hide-${l.id}`}
                                  >
                                    Hide
                                  </Button>
                                )}
                              </div>
                            </div>
                          </Card>
                        ))}
                      </div>
                    )}
                  </div>
                )}


                {/* Equipment */}
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-lg font-semibold text-[#1e3a5f] flex items-center gap-2">
                      <TruckIcon className="w-5 h-5" />
                      Equipment ({userDetails?.equipment?.length || 0})
                    </h3>
                    {selectedUser && ((selectedUser.roles && selectedUser.roles.includes('carrier')) || selectedUser.role === 'carrier') && (
                      <Button
                        size="sm"
                        onClick={() => setShowEquipmentForm(true)}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        Add Equipment
                      </Button>
                    )}
                  </div>
                  
                  {!userDetails ? (
                    <p className="text-gray-500">Loading...</p>
                  ) : userDetails.equipment.length === 0 ? (
                    <Card className="p-6 text-center">
                      <p className="text-gray-600">No equipment registered yet</p>
                    </Card>
                  ) : (
                    <div className="space-y-3">
                      {userDetails.equipment.map((equip) => (
                        <Card key={equip.id} className="p-4">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <h4 className="font-semibold text-gray-900 mb-2 capitalize">
                                {equip.equipment_type.replace(/_/g, ' ')} - {equip.description}
                              </h4>
                              <div className="grid grid-cols-3 gap-3 text-sm">
                                <div>
                                  <p className="text-gray-600">Registration</p>
                                  <p className="font-medium">{equip.registration_number}</p>
                                </div>
                                <div>
                                  <p className="text-gray-600">Registration Expiry</p>
                                  <p className="font-medium">{equip.registration_expiry}</p>
                                </div>
                                <div>
                                  <p className="text-gray-600">Equipment Type</p>
                                  <p className="font-medium capitalize">{equip.equipment_type.replace(/_/g, ' ')}</p>
                                </div>
                                {equip.tare_mass && (
                                  <div>
                                    <p className="text-gray-600">Tare Mass</p>
                                    <p className="font-medium">{equip.tare_mass.toLocaleString()} kg</p>
                                  </div>
                                )}
                                {equip.payload_rating && (
                                  <div>
                                    <p className="text-gray-600">Payload Rating</p>
                                    <p className="font-medium">{equip.payload_rating.toLocaleString()} kg</p>
                                  </div>
                                )}
                                {equip.number_of_axles && (
                                  <div>
                                    <p className="text-gray-600">Number of Axles</p>
                                    <p className="font-medium">{equip.number_of_axles}</p>
                                  </div>
                                )}
                                {equip.number_of_tyres && (
                                  <div>
                                    <p className="text-gray-600">Number of Tyres</p>
                                    <p className="font-medium">{equip.number_of_tyres}</p>
                                  </div>
                                )}
                              </div>
                              
                              {/* Equipment Photos */}
                              <div className="mt-3 flex gap-2">
                                {equip.photo_rear_quarter && (
                                  <div>
                                    <p className="text-xs text-gray-500 mb-1">Rear Quarter</p>
                                    <img src={equip.photo_rear_quarter} alt="Rear quarter" className="w-24 h-24 object-cover rounded border" />
                                  </div>
                                )}
                                {equip.photo_front && (
                                  <div>
                                    <p className="text-xs text-gray-500 mb-1">Front</p>
                                    <img src={equip.photo_front} alt="Front" className="w-24 h-24 object-cover rounded border" />
                                  </div>
                                )}
                                {equip.photo_side && (
                                  <div>
                                    <p className="text-xs text-gray-500 mb-1">Side</p>
                                    <img src={equip.photo_side} alt="Side" className="w-24 h-24 object-cover rounded border" />
                                  </div>
                                )}
                                {equip.rating_letter_url && (
                                  <div>
                                    <p className="text-xs text-gray-500 mb-1">Rating Letter</p>
                                    <img src={equip.rating_letter_url} alt="Rating letter" className="w-24 h-24 object-cover rounded border" />
                                  </div>
                                )}
                              </div>
                            </div>
                            
                            {/* Action Buttons */}
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleEditEquip(equip)}
                                className="text-blue-600 border-blue-300"
                              >
                                Edit
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setEquipToDelete({ equip, userEmail: selectedUser.email });
                                  setShowDeleteEquipModal(true);
                                }}
                                className="text-red-600 hover:text-red-700 border-red-300"
                              >
                                Delete
                              </Button>
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <>
          <div 
            className="fixed inset-0 bg-black/50 z-50 backdrop-blur-sm"
            onClick={() => setShowEditModal(false)}
          />
          
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div 
              className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-[#1e3a5f]">Edit User</h2>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <Label>Email (cannot be changed)</Label>
                  <Input value={selectedUser.email} disabled className="bg-gray-100" />
                </div>
                
                <div>
                  <Label>Name</Label>
                  <Input
                    value={editFormData.name}
                    onChange={(e) => setEditFormData({...editFormData, name: e.target.value})}
                  />
                </div>
                
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={editFormData.phone}
                    onChange={(e) => setEditFormData({...editFormData, phone: e.target.value})}
                  />
                </div>
                
                <div>
                  <Label>Company Name</Label>
                  <Input
                    value={editFormData.company_name}
                    onChange={(e) => setEditFormData({...editFormData, company_name: e.target.value})}
                  />
                </div>
                
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={editFormData.is_active}
                    onChange={(e) => setEditFormData({...editFormData, is_active: e.target.checked})}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="is_active" className="cursor-pointer">Active</Label>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    onClick={handleSaveEdit}
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                  >
                    Save Changes
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setShowEditModal(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}


      {/* Add Equipment Modal */}
      {showEquipmentForm && selectedUser && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={() => setShowEquipmentForm(false)} />
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h2 className="text-lg font-bold text-[#1e3a5f] mb-4">Add Equipment for {selectedUser.name}</h2>
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium">Type</label>
                  <select
                    value={newEquipment.type}
                    onChange={(e) => setNewEquipment(p => ({ ...p, type: e.target.value }))}
                    className="w-full h-10 border rounded-md px-3 text-sm"
                  >
                    <option value="prime_mover">Prime Mover</option>
                    <option value="trailer">Trailer</option>
                    <option value="rigid_truck">Rigid Truck</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-sm font-medium">Make *</label>
                    <Input value={newEquipment.make} onChange={(e) => setNewEquipment(p => ({ ...p, make: e.target.value }))} placeholder="e.g. Kenworth" />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Model</label>
                    <Input value={newEquipment.model} onChange={(e) => setNewEquipment(p => ({ ...p, model: e.target.value }))} placeholder="e.g. T909" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-sm font-medium">Year</label>
                    <Input value={newEquipment.year} onChange={(e) => setNewEquipment(p => ({ ...p, year: e.target.value }))} placeholder="e.g. 2022" />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Rego *</label>
                    <Input value={newEquipment.registration_number} onChange={(e) => setNewEquipment(p => ({ ...p, registration_number: e.target.value }))} placeholder="e.g. ABC123" />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium">State</label>
                  <select
                    value={newEquipment.state}
                    onChange={(e) => setNewEquipment(p => ({ ...p, state: e.target.value }))}
                    className="w-full h-10 border rounded-md px-3 text-sm"
                  >
                    {['NSW','VIC','QLD','WA','SA','TAS','NT','ACT'].map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium">Registration Expiry *</label>
                  <Input type="date" value={newEquipment.registration_expiry || ''} onChange={(e) => setNewEquipment(p => ({ ...p, registration_expiry: e.target.value }))} />
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <Button onClick={handleAddEquipment} className="bg-green-600 hover:bg-green-700 text-white flex-1">Save Equipment</Button>
                <Button variant="outline" onClick={() => setShowEquipmentForm(false)} className="flex-1">Cancel</Button>
              </div>
            </div>
          </div>
        </>
      )}



      {/* Insurance Upload Modal */}
      {showInsuranceUploadModal && selectedUser && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={() => setShowInsuranceUploadModal(false)} />
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
                <h2 className="text-xl font-bold text-[#1e3a5f]">
                  Upload Insurance Document for {selectedUser.name}
                </h2>
                <button
                  onClick={() => setShowInsuranceUploadModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmitInsurance} className="p-6 space-y-4">
                <div>
                  <Label htmlFor="insurance_type">Insurance Type *</Label>
                  <select
                    id="insurance_type"
                    name="insurance_type"
                    value={insuranceFormData.insurance_type}
                    onChange={handleInsuranceFormChange}
                    required
                    className="w-full border border-gray-300 rounded-md p-2"
                  >
                    <option value="">Select Document Type</option>
                    <option value="public_liability">Public Liability</option>
                    <option value="motor_fleet">Motor Fleet</option>
                    <option value="marine_carriers">Marine Carriers</option>
                    <option value="workers_comp">Workers Compensation</option>
                    <option value="trailers_in_control">Trailers in Control</option>
                    <option value="nhvr_accreditation">NHVR Accreditation</option>
                    <option value="wahva_certificate">WAHVA Certificate</option>
                    <option value="trucksafe_certificate">TruckSafe Certificate</option>
                    <option value="haccp_accreditation">HACCP Accreditation</option>
                    <option value="msic_licence">MSIC Licence</option>
                    <option value="dangerous_goods_licence">Dangerous Goods Licence</option>
                    <option value="ports_pass">Ports Pass</option>
                    <option value="high_risk_work_licence">High Risk Work Licence</option>
                    <option value="trailer_photo">Trailer Photo</option>
                    <option value="truck_photo">Truck Photo</option>
                    <option value="registration">Vehicle Registration</option>
                  </select>
                </div>

                <div>
                  <Label htmlFor="policy_number">Policy/Certificate Number *</Label>
                  <Input
                    id="policy_number"
                    name="policy_number"
                    value={insuranceFormData.policy_number}
                    onChange={handleInsuranceFormChange}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="insurer_name">Insurer/Issuer Name</Label>
                  <Input
                    id="insurer_name"
                    name="insurer_name"
                    value={insuranceFormData.insurer_name}
                    onChange={handleInsuranceFormChange}
                  />
                </div>

                <div>
                  <Label htmlFor="expiry_date">Expiry Date *</Label>
                  <Input
                    id="expiry_date"
                    name="expiry_date"
                    type="date"
                    value={insuranceFormData.expiry_date}
                    onChange={handleInsuranceFormChange}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="document_file">Document File * (PDF, JPG, PNG - Max 5MB)</Label>
                  <Input
                    id="document_file"
                    name="document_file"
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleInsuranceFileChange}
                    required
                    className="cursor-pointer"
                  />
                  {insuranceFormData.document_file && (
                    <p className="text-sm text-green-600 mt-1">
                      ✓ {insuranceFormData.document_file.name}
                    </p>
                  )}
                </div>

                <div className="flex gap-3 pt-4 border-t">
                  <Button 
                    type="submit"
                    disabled={loading}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {loading ? 'Uploading...' : 'Upload Document'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowInsuranceUploadModal(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}

      {/* Fix Role Modal */}
      {showFixRoleModal && fixRoleData.user && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={() => setShowFixRoleModal(false)} />
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="bg-gradient-to-r from-yellow-500 to-orange-500 px-6 py-4 rounded-t-lg">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <Shield className="w-6 h-6" />
                  Fix User Role
                </h2>
              </div>

              <div className="p-6 space-y-4">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm text-gray-700 mb-2">
                    <strong>User:</strong> {fixRoleData.user.name}
                  </p>
                  <p className="text-sm text-gray-700 mb-2">
                    <strong>Email:</strong> {fixRoleData.user.email}
                  </p>
                  <p className="text-sm text-gray-700">
                    <strong>Company:</strong> {fixRoleData.user.company_name || 'N/A'}
                  </p>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm font-semibold text-blue-900 mb-2">
                    Recommended Role: {fixRoleData.recommendedRole.toUpperCase()}
                  </p>
                  <p className="text-xs text-blue-700">
                    {fixRoleData.recommendedRole === 'carrier' 
                      ? '💼 This user appears to be a CARRIER (transports goods, pays subscription)'
                      : '📦 This user appears to be a SHIPPER (posts loads, free account)'}
                  </p>
                </div>

                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-900">Select role to assign:</p>
                  
                  <Button
                    onClick={() => confirmFixRole('carrier')}
                    disabled={loading}
                    className="w-full bg-orange-600 hover:bg-orange-700 text-white"
                  >
                    <TruckIcon className="w-4 h-4 mr-2" />
                    {loading ? 'Processing...' : 'Set as CARRIER (Transporter)'}
                  </Button>
                  
                  <Button
                    onClick={() => confirmFixRole('shipper')}
                    disabled={loading}
                    className="w-full bg-green-600 hover:bg-green-700 text-white"
                  >
                    <Users className="w-4 h-4 mr-2" />
                    {loading ? 'Processing...' : 'Set as SHIPPER (Customer)'}
                  </Button>
                  
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-300"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-white text-gray-500">or</span>
                    </div>
                  </div>
                  
                  <Button
                    onClick={() => confirmFixRole('both')}
                    disabled={loading}
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    <Users className="w-4 h-4 mr-2" />
                    <TruckIcon className="w-4 h-4 mr-2" />
                    {loading ? 'Processing...' : 'Set as BOTH (Shipper + Carrier)'}
                  </Button>
                </div>
              </div>

              <div className="bg-gray-50 px-6 py-4 rounded-b-lg flex justify-end">
                <Button
                  type="button"
                  variant="outline"
                  disabled={loading}
                  onClick={() => {
                    setShowFixRoleModal(false);
                    setFixRoleData({ user: null, recommendedRole: 'carrier' });
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Approve Carrier Modal */}
      {showApproveCarrierModal && carrierToApprove && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={() => setShowApproveCarrierModal(false)} />
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="bg-gradient-to-r from-green-500 to-emerald-600 px-6 py-4 rounded-t-lg">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <CheckCircle className="w-6 h-6" />
                  Approve Carrier
                </h2>
              </div>

              <div className="p-6 space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-gray-700 mb-2">
                    <strong>Carrier:</strong> {carrierToApprove.name}
                  </p>
                  <p className="text-sm text-gray-700 mb-2">
                    <strong>Email:</strong> {carrierToApprove.email}
                  </p>
                  <p className="text-sm text-gray-700">
                    <strong>Company:</strong> {carrierToApprove.company_name || 'N/A'}
                  </p>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm text-yellow-900">
                    <strong>⚠️ Before approving, verify:</strong>
                  </p>
                  <ul className="text-xs text-yellow-800 mt-2 space-y-1 ml-4 list-disc">
                    <li>Insurance documents uploaded</li>
                    <li>Payment completed</li>
                    <li>Company details verified</li>
                  </ul>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-sm font-semibold text-green-900 mb-1">
                    ✓ This will allow the carrier to:
                  </p>
                  <ul className="text-xs text-green-800 space-y-1 ml-4 list-disc">
                    <li>Submit quotes on available loads</li>
                    <li>Access full platform features</li>
                    <li>Start transporting goods</li>
                  </ul>
                </div>
              </div>

              <div className="bg-gray-50 px-6 py-4 rounded-b-lg flex justify-end gap-3">
                <Button
                  type="button"
                  variant="outline"
                  disabled={loading}
                  onClick={() => {
                    setShowApproveCarrierModal(false);
                    setCarrierToApprove(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={confirmApproveCarrier}
                  disabled={loading}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {loading ? 'Approving...' : 'Approve Carrier'}
                </Button>
              </div>
            </div>
          </div>
        </>
      )}

      <ConfirmModal
        isOpen={showDeleteUserModal}
        onClose={() => {
          setShowDeleteUserModal(false);
          setUserToDelete(null);
        }}
        onConfirm={confirmDeleteUser}
        title="Delete User"
        message={userToDelete ? `Are you sure you want to delete ${userToDelete.name} (${userToDelete.email})?\n\nThis will also delete all their related data (loads, quotes, insurance, equipment).\n\nThis action cannot be undone.` : ''}
        confirmText="Delete User"
        variant="danger"
        loading={loading}
      />

      <ConfirmModal
        isOpen={showUnsuspendModal}
        onClose={() => {
          setShowUnsuspendModal(false);
          setUserToUnsuspend(null);
        }}
        onConfirm={confirmUnsuspendUser}
        title="Unsuspend User"
        message={userToUnsuspend ? `Unsuspend ${userToUnsuspend.name}'s account?` : ''}
        confirmText="Unsuspend"
        variant="success"
        loading={loading}
      />

      <ConfirmModal
        isOpen={showDeleteDocModal}
        onClose={() => {
          setShowDeleteDocModal(false);
          setDocToDelete(null);
        }}
        onConfirm={confirmDeleteDoc}
        title="Delete Document"
        message="Are you sure you want to delete this document? This action cannot be undone."
        confirmText="Delete Document"
        variant="danger"
        loading={loading}
      />

      <ConfirmModal
        isOpen={showDeleteEquipModal}
        onClose={() => {
          setShowDeleteEquipModal(false);
          setEquipToDelete(null);
        }}
        onConfirm={confirmDeleteEquip}
        title="Delete Equipment"
        message={equipToDelete ? `Are you sure you want to delete ${equipToDelete.equip.registration_number}? This action cannot be undone.` : ''}
        confirmText="Delete Equipment"
        variant="danger"
        loading={loading}
      />

      {/* Edit Equipment Modal */}
      {showEditEquipModal && equipToEdit && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={() => setShowEditEquipModal(false)} />
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 my-8">
              <h2 className="text-lg font-bold text-[#1e3a5f] mb-4">Edit Equipment</h2>
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium">Type</label>
                  <select
                    value={editEquipData.equipment_type}
                    onChange={(e) => setEditEquipData(p => ({ ...p, equipment_type: e.target.value }))}
                    className="w-full h-10 border rounded-md px-3 text-sm"
                  >
                    <option value="prime_mover">Prime Mover</option>
                    <option value="trailer">Trailer</option>
                    <option value="rigid_truck">Rigid Truck</option>
                    <option value="flat_top_open">Flat Top Open</option>
                    <option value="tautliner">Tautliner</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium">Description *</label>
                  <Input value={editEquipData.description} onChange={(e) => setEditEquipData(p => ({ ...p, description: e.target.value }))} placeholder="e.g. Kenworth T909 2022" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-sm font-medium">Rego *</label>
                    <Input value={editEquipData.registration_number} onChange={(e) => setEditEquipData(p => ({ ...p, registration_number: e.target.value }))} />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Rego Expiry *</label>
                    <Input type="date" value={editEquipData.registration_expiry} onChange={(e) => setEditEquipData(p => ({ ...p, registration_expiry: e.target.value }))} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-sm font-medium">Tare Mass (kg)</label>
                    <Input type="number" value={editEquipData.tare_mass} onChange={(e) => setEditEquipData(p => ({ ...p, tare_mass: e.target.value }))} />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Payload Rating (kg)</label>
                    <Input type="number" value={editEquipData.payload_rating} onChange={(e) => setEditEquipData(p => ({ ...p, payload_rating: e.target.value }))} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-sm font-medium">Number of Axles</label>
                    <Input type="number" value={editEquipData.number_of_axles} onChange={(e) => setEditEquipData(p => ({ ...p, number_of_axles: e.target.value }))} />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Number of Tyres</label>
                    <Input type="number" value={editEquipData.number_of_tyres} onChange={(e) => setEditEquipData(p => ({ ...p, number_of_tyres: e.target.value }))} />
                  </div>
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <Button onClick={confirmEditEquip} disabled={loading} className="bg-blue-600 hover:bg-blue-700 text-white flex-1">
                  {loading ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button variant="outline" onClick={() => { setShowEditEquipModal(false); setEquipToEdit(null); }} className="flex-1">Cancel</Button>
              </div>
            </div>
          </div>
        </>
      )}

      
      <Footer />
    </div>
  );
};

export default AdminUsers;
